import { ReloadOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Drawer, Form } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useState } from "react"

import { DateRangeFilter } from "../../../../components/Filters/DateRangeFilter.tsx"
import { PresetDateRangeFilter } from "../../../../components/Filters/PresetDateRangeFilter.tsx"
import { DebouncedInput } from "../../../../components/Input/DebouncedInput.tsx"
import { ResolvedSelect } from "../../../../components/Select/ResolvedSelect.tsx"
import {
  type FilterField,
  asString,
  countActiveFilters,
  fieldKeys
} from "../../redux/projects.constants.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"

interface ProjectListGridFiltersDrawerProps {
  open: boolean
  filters: ProjectFilters
  filterOptions: Record<string, BaseOptionType[]>
  filterFields: FilterField[]
  onClose: () => void
  onFiltersChange: (data: Partial<ProjectFilters>) => void
}

export function ProjectListGridFiltersDrawer({
  open,
  filters,
  filterOptions,
  filterFields,
  onClose,
  onFiltersChange
}: ProjectListGridFiltersDrawerProps) {
  const [form] = Form.useForm()
  const [resetToken, setResetToken] = useState(0)

  useEffect(() => {
    if (open) {
      form.setFieldsValue(filters)
    }
  }, [open, filters, form])

  const handleValuesChange = (changedValues: Partial<ProjectFilters>) => {
    const normalized = Object.fromEntries(
      Object.entries(changedValues).map(([key, value]) => [
        key,
        value === undefined ? null : value
      ])
    )
    onFiltersChange(normalized)
  }

  const handleReset = () => {
    const resetValues = filterFields.reduce<Partial<ProjectFilters>>(
      (acc, field) => {
        fieldKeys(field).forEach((key) => {
          acc[key] = null as never
        })
        return acc
      },
      {}
    )
    form.resetFields()
    onFiltersChange(resetValues)
    // Clearing a field that already reads null is no value change, so the
    // debounced fields need telling — see the KB decision on these filters.
    setResetToken((token) => token + 1)
  }

  const activeFiltersCount = countActiveFilters(filters, filterFields)

  return (
    <Drawer
      title="Filters"
      placement="right"
      open={open}
      onClose={onClose}
      width={360}
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={handleReset}
          disabled={activeFiltersCount === 0}
        >
          Reset
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={filters}
        onValuesChange={handleValuesChange}
      >
        {filterFields.map((field) => {
          // Neither field below fits the form's value contract: one commits on
          // its own schedule, the other writes two params.
          if (field.type === "text") {
            return (
              <Form.Item key={field.key} label={field.label}>
                <DebouncedInput
                  resetSignal={resetToken}
                  prefix={<SearchOutlined className="text-gray-500" />}
                  placeholder={field.placeholder ?? field.label}
                  value={asString(filters[field.key])}
                  onValueChange={(value) =>
                    onFiltersChange({ [field.key]: value })
                  }
                />
              </Form.Item>
            )
          }

          if (field.type === "presetrange") {
            return (
              <Form.Item key={field.key} label={field.label}>
                <PresetDateRangeFilter
                  presets={field.presets}
                  preset={asString(filters[field.presetKey])}
                  after={asString(filters[field.key])}
                  before={asString(filters[field.endKey])}
                  presetKey={field.presetKey}
                  afterKey={field.key}
                  beforeKey={field.endKey}
                  label={field.label}
                  placeholder={field.placeholder ?? field.label}
                  allTimeLabel={field.allTimeLabel}
                  customLabel={field.customLabel}
                  className={"w-full"}
                  onChange={onFiltersChange}
                />
              </Form.Item>
            )
          }

          if (field.type === "daterange") {
            return (
              <Form.Item key={field.key} label={field.label}>
                <DateRangeFilter
                  after={asString(filters[field.key])}
                  before={asString(filters[field.endKey])}
                  afterKey={field.key}
                  beforeKey={field.endKey}
                  placeholder={field.placeholder ?? field.label}
                  className={"w-full"}
                  onChange={onFiltersChange}
                />
              </Form.Item>
            )
          }

          const options = filterOptions?.[field.key] ?? []
          const isMultiselect = field.type === "multiselect"

          return (
            <Form.Item key={field.key} name={field.key} label={field.label}>
              <ResolvedSelect
                allowClear
                showSearch
                mode={isMultiselect ? "multiple" : undefined}
                placeholder={field.placeholder ?? `Select ${field.label}`}
                options={options}
                optionFilterProp="label"
                style={{ width: "100%" }}
              />
            </Form.Item>
          )
        })}
      </Form>
    </Drawer>
  )
}
